/**
 * Records the input of a battle as a CommandLog in global battle ticks (proc.battle_tick): one frame per tick,
 * run-length encoded, one turn per move of the simulation (a turn starts in the tick in which proc.new_move started
 * its move; turn 0 starts at tick 1). This is the log the backend re-simulates (BattleRecord.log) and the one the
 * legacy ROUNDS record can be derived from (CommandLog.toLegacyRounds). Browser-safe.
 */
import { CommandLog, type CommandLogData } from '../../session/command-log';
import { type InputCommand, type InputFrame, cloneFrame, framesEqual, isEmptyFrame, isOneShotFrame } from '../../session/input';

export class BattleRecorder {
  private readonly log: CommandLog;
  private run: InputCommand | null = null;
  private last = 0;
  private closed = false;

  constructor(firstTeam = 1, startTick = 1) {
    this.log = CommandLog.create(firstTeam, startTick);
  }

  /** Last tick recorded (0 before the first). */
  get lastTick(): number {
    return this.last;
  }

  /** Input of `tick` (ticks must increase). Empty frames are gaps of the log. */
  record(tick: number, frame: InputFrame): void {
    if (this.closed) throw new Error('BattleRecorder: the battle is over');
    if (tick <= this.last) throw new Error(`BattleRecorder: tick ${tick} is not after ${this.last}`);
    this.last = tick;
    if (isEmptyFrame(frame)) {
      this.closeRun();
    } else if (isOneShotFrame(frame)) {
      this.closeRun();
      this.push({ tick, frame: cloneFrame(frame), repeat: 0 });
    } else if (this.run && framesEqual(this.run.frame, frame) && this.run.tick + this.run.repeat === tick - 1) {
      this.run.repeat++;
    } else {
      this.closeRun();
      this.run = { tick, frame: cloneFrame(frame), repeat: 0 };
    }
  }

  /** A new move of `team` starts in `tick` (proc.new_move): the current turn ends at `tick - 1`. */
  newMove(tick: number, team: number): void {
    if (this.closed) return;
    this.closeRun();
    const cur = this.log.current;
    if (tick - 1 < cur.startTick) {
      // A move without a single tick of its own (cannot happen in proc: a move lasts at least until check_ready).
      cur.team = team;
      return;
    }
    const err = this.log.endTurn(cur.turn, tick - 1, team);
    if (err) throw new Error(`BattleRecorder: ${err}`);
  }

  /** The battle ended; `tick` is its last tick. */
  finish(tick: number): void {
    if (this.closed) return;
    this.closeRun();
    this.log.close(Math.max(tick, this.last));
    this.closed = true;
  }

  get finished(): boolean {
    return this.closed;
  }

  toJSON(): CommandLogData {
    // An open recording is exported as it stands (the current run included, the open turn confirmed up to the
    // last recorded tick).
    const data = this.log.toJSON();
    if (!this.closed) {
      const cur = data.turns[data.turns.length - 1];
      if (this.run) cur.commands.push({ tick: this.run.tick, frame: cloneFrame(this.run.frame), repeat: this.run.repeat });
      cur.throughTick = Math.max(cur.throughTick, this.last);
    }
    return data;
  }

  private closeRun(): void {
    if (!this.run) return;
    const r = this.run;
    this.run = null;
    this.push(r);
  }

  private push(cmd: InputCommand): void {
    const cur = this.log.current;
    const err = this.log.append(cur.turn, [cmd], cmd.tick + cmd.repeat);
    if (err) throw new Error(`BattleRecorder: ${err}`);
  }
}
